import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Search, User } from "lucide-react";
import { GlassCard } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface DirectoryUser {
  id: string;
  username: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  department: string | null;
  title: string | null;
  is_active: boolean;
  last_login: string | null;
}

interface TagUsersTabProps {
  tagId: string;
}

export const TagUsersTab = ({ tagId }: TagUsersTabProps) => {
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        
        // Get user IDs assigned to this tag
        const { data: userTagsData, error: userTagsError } = await supabase
          .from("device_user_tags")
          .select("device_user_id")
          .eq("tag_id", tagId);

        if (userTagsError) throw userTagsError;

        const userIds = userTagsData?.map(ut => ut.device_user_id) || [];

        if (userIds.length === 0) {
          setUsers([]);
          return;
        }

        // Fetch the actual users
        const { data: usersData, error: usersError } = await supabase
          .from("device_users")
          .select("*")
          .in("id", userIds);

        if (usersError) throw usersError;

        setUsers(usersData || []);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [tagId]);

  const filteredUsers = users.filter(user => {
    const displayName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username;
    return displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getInitials = (user: DirectoryUser) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user.username.substring(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchQuery ? "No users found matching your search" : "No users assigned to this tag"}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredUsers.map((user) => {
            const displayName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username;
            return (
              <Link key={user.id} to={`/admin/directory-users/${user.id}`}>
                <GlassCard className="p-4 hover:shadow-md transition-shadow cursor-pointer h-full">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {getInitials(user)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-foreground truncate">{displayName}</h3>
                        <Badge variant={user.is_active ? "default" : "secondary"} className="text-xs">
                          {user.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      {user.email && (
                        <p className="text-sm text-muted-foreground truncate mb-1">
                          {user.email}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {user.department && <span>{user.department}</span>}
                        {user.title && (
                          <>
                            <span>•</span>
                            <span>{user.title}</span>
                          </>
                        )}
                      </div>
                      {user.last_login && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Last login: {new Date(user.last_login).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </GlassCard>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
